/**
 * Centralized navigation visibility and active state logic
 * Ensures consistent behavior across the entire app
 */

export type UserRole = 'guest' | 'buyer' | 'seller' | 'admin';

/**
 * Determines if bottom navigation bar should be visible
 * 
 * SHOW on all buyer-facing routes:
 * - Home, Categories, Explore, Product pages
 * - Cart, Wishlist
 * - Orders, Returns, Chats, Tickets
 * - Account and all account subpages
 * - Blog, Stories, About pages
 * 
 * HIDE on:
 * - Seller dashboard (/dashboard*, /seller*)
 * - Admin panel (/admin*)
 * - Auth pages (/auth/*)
 */
export function shouldShowBottomNav(pathname: string, isMobile: boolean): boolean {
  // Only show on mobile
  if (!isMobile) return false;

  // Hide on admin routes
  if (pathname.startsWith('/admin')) return false;

  // Hide on seller routes
  if (pathname.startsWith('/dashboard') || pathname.startsWith('/seller')) return false;

  // Hide on auth pages
  if (pathname.startsWith('/auth')) return false;

  // Show on all other routes (buyer-facing public pages)
  return true;
}

/**
 * Determines if a nav item is active based on current pathname
 * Handles special cases for nested routes
 */
export function isNavItemActive(itemPath: string, currentPathname: string): boolean {
  // Home is only active on exact match
  if (itemPath === '/') {
    return currentPathname === '/';
  }

  // Categories active on /categories and all subpages
  if (itemPath === '/categories') {
    return currentPathname.startsWith('/categories') || currentPathname.startsWith('/catalog');
  }

  // Explore active on /explore and all region subpages
  if (itemPath === '/explore') {
    return currentPathname.startsWith('/explore');
  }

  // Favorites/Wishlist active on exact match
  if (itemPath === '/favorites' || itemPath === '/wishlist') {
    return currentPathname === '/favorites' || currentPathname === '/wishlist';
  }

  // Profile/Account active on:
  // - /account and all subpages
  // - /orders and order details
  // - /returns
  // - /buyer/chats (buyer chats)
  // - /tickets and ticket details
  if (itemPath === '/account') {
    return (
      currentPathname.startsWith('/account') ||
      currentPathname.startsWith('/orders') ||
      currentPathname === '/returns' ||
      currentPathname.startsWith('/buyer/chats') ||
      currentPathname.startsWith('/tickets')
    );
  }

  // Default: check if path starts with item path
  return currentPathname.startsWith(itemPath);
}
